import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { Loading } from 'framework/ui/mui-builder';
import { PermissionsMap, SecurityContextTemplate, useGlobalSecurityReactContext } from 'framework/authorization';
import { AccessInfo, ProjectSecurityContext } from 'domain/shared/newProject';
import { GET_PROJECT_ASSUMPTION } from '../graphql/assumptionQueries';
import { AssumptionType, ProjectAssumptionResponse } from '../types';

interface AssumptionReviewContextValue {
  assumption: AssumptionType;
  projectId: string;
  refetch: () => Promise<unknown>;
}

const AssumptionReviewDataContext = createContext<AssumptionReviewContextValue | null>(null);

export const useAssumptionReview = (): AssumptionReviewContextValue => {
  const ctx = useContext(AssumptionReviewDataContext);
  if (!ctx) {
    throw new Error('useAssumptionReview must be used within an AssumptionReviewContext');
  }
  return ctx;
};

interface SecurityWrapperProps extends PropsWithChildren {
  id: string | undefined;
  accessInfo: AccessInfo | undefined;
  permissionToCheck: string | string[];
}

const AssumptionReviewSecurityWrapper = ({ id, accessInfo, permissionToCheck, children }: SecurityWrapperProps) => {
  const globalContext = useGlobalSecurityReactContext() || {};

  const projectPermissionsInGlobalFormat = useMemo(
    () =>
      accessInfo?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [],
    [accessInfo],
  );

  return (
    <SecurityContextTemplate
      id={id}
      getPermissionsFunc={() =>
        Promise.all([
          Promise.resolve(Object.keys(globalContext.permissions as unknown as PermissionsMap) || []),
          Promise.resolve(projectPermissionsInGlobalFormat),
        ]).then(permissions => permissions.reduce((flatten, arr) => [...flatten, ...arr]))
      }
      contextName={'AssumptionReviewSecurity'}
      permissionToCheck={permissionToCheck}
    >
      <ProjectSecurityContext permissions={accessInfo}>{children}</ProjectSecurityContext>
    </SecurityContextTemplate>
  );
};

export const AssumptionReviewContext = ({
  permissionToCheck,
  children,
}: PropsWithChildren<{ permissionToCheck: string | string[] }>) => {
  const { id, assumptionId } = useParams<Dictionary<string>>();
  const { data, loading, refetch } = useQuery<ProjectAssumptionResponse>(GET_PROJECT_ASSUMPTION, {
    variables: { id: id! },
    skip: !id,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const accessInfo = data?.baseProject?.accessInfo;
  const assumption = data?.baseProject?.assumptions?.find(item => item.id === assumptionId) ?? null;

  const contextValue = useMemo<AssumptionReviewContextValue | null>(
    () => (assumption ? { assumption, projectId: id!, refetch } : null),
    [assumption, id, refetch],
  );

  if (loading && !assumption) {
    return <Loading isInitialPosition />;
  }

  if (!contextValue) {
    return null;
  }

  return (
    <AssumptionReviewSecurityWrapper id={id} accessInfo={accessInfo} permissionToCheck={permissionToCheck}>
      <AssumptionReviewDataContext.Provider value={contextValue}>{children}</AssumptionReviewDataContext.Provider>
    </AssumptionReviewSecurityWrapper>
  );
};
